import { useState, useEffect, useRef } from 'react';
import * as React from 'react';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { SaveClientFlags } from '@sdk/authentication/types';

import { sendPsatFeedback, shouldDisplayPsatScore } from '@web/components/psatScore/utils';
import { PsatScoreSteps } from '@web/components/psatScore/PsatScoreSteps';
import PopupSurveyContainer from '@web/components/survey/PopupSurveyContainer';
import SurveyCloseButton from '@web/components/survey/SurveyCloseButton';
import { setCountdownDate } from '@web/components/externalRate/utils';

type Props = {
  user: AuthUserType;
  usersCount: number;
  locationsCount: number;
  subscriptionName: string;
  saveClientFlags: SaveClientFlags;
  forceDisplay: boolean;
  onClose?: () => void;
};

export const PsatScore: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  subscriptionName,
  usersCount,
  locationsCount,
  saveClientFlags,
  forceDisplay,
  onClose,
}) => {
  const feedbackSentRef = useRef(false);
  const wasDisplayedRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const [score, setScore] = useState(null);
  const [comment1, setComment1] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const close = (): void => {
    if (!feedbackSentRef.current) {
      sendPsatFeedback(
        { comment1, score },
        { user, subscriptionName, usersCount, saveClientFlags, locationsCount }
      );
    }

    onClose?.();
    setIsVisible(false);
  };

  useEffect(() => {
    if (forceDisplay || (shouldDisplayPsatScore(user) && !wasDisplayedRef.current)) {
      setIsVisible(true);
      wasDisplayedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceDisplay]);

  useEffect(() => {
    if (!feedbackSentRef.current && submitted) {
      sendPsatFeedback(
        { comment1, score },
        { user, subscriptionName, usersCount, saveClientFlags, locationsCount }
      );
      feedbackSentRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment1, score, submitted, subscriptionName, user, usersCount]);

  const onPositiveRate = (): void => {
    setCountdownDate(user.settings.clientFlags, saveClientFlags);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <PopupSurveyContainer width={330}>
      <SurveyCloseButton onClose={close} />
      <PsatScoreSteps
        score={score}
        setScore={setScore}
        comment1={comment1}
        setComment1={setComment1}
        submitForm={(): void => setSubmitted(true)}
        close={close}
        onPositiveRate={onPositiveRate}
      />
    </PopupSurveyContainer>
  );
};
